import React from "react";
import { Box, Typography } from "@mui/material";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { QUIZ } from "@/theme/quizTokens";

const PLACEHOLDER =
  "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/placeholder-meal.png?v=1746201592";

// Per-card accent (Figma cycles through soft brand tints).
const ACCENTS = ["#cced85", "#e5d1f2", "#b8e6d2", "#c2edfa", "#f5c2bf"];
function accentFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
}

type Props = {
  image?: string;
  title: string;
  subtitle?: string; // weight ("600G") or price ("£5.99")
  description?: string;
  count: number;
  onIncrement: () => void;
  onDecrement: () => void;
  /** Seed for the accent colour (defaults to the title). */
  accentSeed?: string;
};

/**
 * Shared meal/treat card from the Figma results redesign: image + RL Aqva name +
 * subtitle + optional description, with a full-width [− qty +] stepper whose centre
 * fill uses a soft per-card accent.
 */
export default function QuizProductCard({
  image,
  title,
  subtitle,
  description,
  count,
  onIncrement,
  onDecrement,
  accentSeed,
}: Props) {
  const accent = accentFor(accentSeed ?? title);

  return (
    <Box
      sx={{
        background: QUIZ.color.white,
        borderRadius: "18px",
        p: "14px",
        border: `1px solid ${accent}`,
        boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* image + name + subtitle */}
      <Box sx={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
        <Box
          component="img"
          src={image || PLACEHOLDER}
          alt={title}
          onError={(e: any) => {
            e.currentTarget.src = PLACEHOLDER;
          }}
          sx={{
            width: 60,
            height: 60,
            borderRadius: "10px",
            objectFit: "cover",
            flexShrink: 0,
            background: QUIZ.color.cream,
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: QUIZ.font.display,
              fontWeight: 900,
              textTransform: "uppercase",
              fontSize: { xs: 16, md: 17 },
              lineHeight: 1.1,
              color: QUIZ.color.ink,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: 13,
                color: QUIZ.color.statLabel,
                mt: "2px",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {description && (
        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontSize: 14,
            lineHeight: 1.35,
            color: QUIZ.color.grey,
          }}
        >
          {description}
        </Typography>
      )}

      {/* stepper bar */}
      <Box
        sx={{
          mt: "auto",
          display: "flex",
          alignItems: "stretch",
          borderRadius: "12px",
          overflow: "hidden",
          height: 44,
        }}
      >
        <Box
          role="button"
          onClick={onDecrement}
          sx={{
            width: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: QUIZ.color.cream2,
            color: QUIZ.color.brown,
            cursor: count <= 0 ? "default" : "pointer",
            opacity: count <= 0 ? 0.45 : 1,
            "&:hover": { opacity: count <= 0 ? 0.45 : 0.85 },
          }}
        >
          <RemoveRoundedIcon />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: accent,
            fontFamily: QUIZ.font.display,
            fontWeight: 900,
            fontSize: 18,
            color: QUIZ.color.ink,
          }}
        >
          {count}
        </Box>
        <Box
          role="button"
          onClick={onIncrement}
          sx={{
            width: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: QUIZ.color.cream2,
            color: QUIZ.color.brown,
            cursor: "pointer",
            "&:hover": { opacity: 0.85 },
          }}
        >
          <AddRoundedIcon />
        </Box>
      </Box>
    </Box>
  );
}
